import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type {
    ComponentSchema,
    ScreenMetadata,
    ScreenPayload,
} from '@/types/sduiTypes'
import type { RootState } from '@/store/rootReducer'
import BaseService from '@/lib/BaseService'
import { computeSeededValue, isBindingDrivenField } from '@/features/it-process/application-management/sdui/services/ObjService'

/**
 * Per-field form state. `seeded` is the value derived from the selected row /
 * defaults; `value` is what the user currently sees (== seeded until they edit);
 * `dirty` marks a user edit so a later re-seed (ancestor row change) skips it.
 */
export interface SDUIFieldState {
    value: unknown
    seeded: unknown
    dirty: boolean
}

/** One screen's cached schema + editable form state, keyed by buildScreenKey. */
export interface SDUIScreenEntry {
    uiSchema: ComponentSchema[]
    metadata: ScreenMetadata
    actions?: ScreenPayload['actions']
    fields: Record<string, SDUIFieldState>
    fieldErrors: Record<string, string[]>
    isLoading: boolean
    error: string | null
}

export interface SDUIState {
    /** Editable form state cache, one entry per screen key. */
    screensByKey: Record<string, SDUIScreenEntry>
    /** Key of the screen currently rendered/active (for external readers, e.g. DAL). */
    activeScreenKey: string | null
    /** Component ids with an action in flight (global; small, transient). */
    loadingActions: string[]
    /** Incremented by delete/mutation actions to trigger a SDUIGrid re-fetch. */
    gridRefreshSeq: number
    /** Per-DTS refresh counters � only the targeted grid re-fetches. */
    gridRefreshSeqByDts: Record<number, number>
    /**
     * Screen keys currently in "new record" mode (set by an "Add" DAL action).
     * While a key is flagged, DynamicScreen seeds that screen from a NULL row �
     * ignoring the grid's auto-selected/ancestor row � so the form opens blank.
     * Cleared by an explicit user row-click (see gridActions) or the Add flow's
     * Save/Cancel, so the normal row-binding resumes afterwards.
     */
    /**
     * Seed row stored per new-record key. `null` means blank form;
     * a plain object means seed specific fields (e.g. pre-fill Licensor).
     */
    newRecordByKey: Record<string, Record<string, unknown> | null>
}

export const initialState: SDUIState = {
    screensByKey: {},
    activeScreenKey: null,
    loadingActions: [],
    gridRefreshSeq: 0,
    gridRefreshSeqByDts: {},
    newRecordByKey: {},
}

/**
 * Cache key for a screen's editable state. Includes baId + languageId so edits
 * for one BA/language context are never read in another. Falls back to a
 * screenId-derived key for the inline screenId path (no dtsId).
 */
export const buildScreenKey = (params: {
    dtsId?: number
    baId?: number
    languageId?: number
    screenId?: string
}): string => {
    if (params.screenId != null) return `screen:${params.screenId}`
    return `${params.dtsId ?? 'none'}:${params.baId ?? 'none'}:${
        params.languageId ?? 'none'
    }`
}

/** Build the per-field state map for a freshly seeded screen payload. */
const buildFields = (
    payload: ScreenPayload,
): Record<string, SDUIFieldState> => {
    const fields: Record<string, SDUIFieldState> = {}
    for (const schema of payload.uiSchema) {
        const field = schema.dataBinding?.field
        if (!field) continue
        const seeded = payload.dataModel?.[field]
        fields[field] = { value: seeded, seeded, dirty: false }
    }
    return fields
}

/**
 * Upsert a screen entry from a (seeded) payload. First visit creates the entry;
 * re-entry (StrictMode re-invoke, remount, re-render) refreshes schema/metadata
 * and the per-field seed, but only overwrites `value` for fields the user has NOT
 * edited � this is what preserves edits across unmount/remount.
 */
const upsertEntry = (
    state: SDUIState,
    key: string,
    payload: ScreenPayload,
): void => {
    const existing = state.screensByKey[key]
    if (!existing) {
        state.screensByKey[key] = {
            uiSchema: payload.uiSchema,
            metadata: payload.metadata,
            actions: payload.actions,
            fields: buildFields(payload),
            fieldErrors: {},
            isLoading: false,
            error: null,
        }
        return
    }

    existing.uiSchema = payload.uiSchema
    existing.metadata = payload.metadata
    existing.actions = payload.actions
    existing.isLoading = false
    existing.error = null
    for (const schema of payload.uiSchema) {
        const field = schema.dataBinding?.field
        if (!field) continue
        const newSeed = payload.dataModel?.[field]
        const fld = existing.fields[field]
        if (!fld) {
            existing.fields[field] = {
                value: newSeed,
                seeded: newSeed,
                dirty: false,
            }
        } else {
            fld.seeded = newSeed
            if (!fld.dirty) fld.value = newSeed
        }
    }
}

/**
 * Async thunk to fetch a screen schema by screenId (inline/DevPlayGround path).
 * The DTS path uses RTK Query (objApi) instead.
 */
export const fetchScreenSchema = createAsyncThunk(
    'sdui/fetchScreenSchema',
    async (screenId: string, { rejectWithValue }) => {
        try {
            const response = await BaseService.get<ScreenPayload>(
                `/api/screens/${screenId}`,
            )
            return response.data
        } catch (error) {
            return rejectWithValue(
                error instanceof Error
                    ? error.message
                    : 'Failed to fetch screen schema',
            )
        }
    },
)

/**
 * Reset an entry's binding-driven fields to a fresh seed and clear their dirty
 * flags + errors. Shared by `resetBoundFields` and `enterNewRecordMode` so the
 * blank/fresh-form logic lives in one place. `selectedRow` null ? blank seed.
 */
const resetEntryBoundFields = (
    entry: SDUIScreenEntry,
    selectedRow: Record<string, unknown> | null,
    objNames?: string[],
): void => {
    const errors = { ...entry.fieldErrors }
    let errorsTouched = false
    for (const schema of entry.uiSchema) {
        const field = schema.dataBinding?.field
        if (!field || !isBindingDrivenField(schema)) continue
        if (objNames && !objNames.includes(schema.id)) continue
        const seed = computeSeededValue(schema, selectedRow)
        entry.fields[field] = { value: seed, seeded: seed, dirty: false }
        if (errors[field]) {
            delete errors[field]
            errorsTouched = true
        }
    }
    if (errorsTouched) entry.fieldErrors = errors
}

const sduiSlice = createSlice({
    name: 'sdui',
    initialState,
    reducers: {
        /** Seed/refresh a screen's cache entry (non-clobbering on re-entry). */
        upsertScreen: (
            state,
            action: PayloadAction<{ key: string; payload: ScreenPayload }>,
        ) => {
            upsertEntry(state, action.payload.key, action.payload.payload)
        },
        /** Mark which screen is active so external readers (DAL) resolve it. */
        setActiveScreenKey: (state, action: PayloadAction<string | null>) => {
            state.activeScreenKey = action.payload
        },
        /** Record a user edit: set the value, mark dirty, clear the field error. */
        updateDataModelField: (
            state,
            action: PayloadAction<{
                key: string
                field: string
                value: unknown
            }>,
        ) => {
            const { key, field, value } = action.payload
            const entry = state.screensByKey[key]
            if (!entry) return
            const fld = entry.fields[field]
            if (fld) {
                fld.value = value
                fld.dirty = true
            } else {
                entry.fields[field] = { value, seeded: value, dirty: true }
            }
            if (entry.fieldErrors[field]) {
                const next = { ...entry.fieldErrors }
                delete next[field]
                entry.fieldErrors = next
            }
        },
        /**
         * Re-seed binding-driven fields from a new selected row. Only fields the
         * user has NOT edited are updated; dirty fields are preserved.
         */
        reseedBoundFields: (
            state,
            action: PayloadAction<{
                key: string
                selectedRow: Record<string, unknown> | null
            }>,
        ) => {
            const { key, selectedRow } = action.payload
            const entry = state.screensByKey[key]
            if (!entry) return
            for (const schema of entry.uiSchema) {
                const field = schema.dataBinding?.field
                if (!field || !isBindingDrivenField(schema)) continue
                const newSeed = computeSeededValue(schema, selectedRow)
                const fld = entry.fields[field]
                if (!fld) {
                    entry.fields[field] = {
                        value: newSeed,
                        seeded: newSeed,
                        dirty: false,
                    }
                } else {
                    fld.seeded = newSeed
                    if (!fld.dirty) fld.value = newSeed
                }
            }
        },
        /**
         * Force-reset bound fields to a fresh seed and clear their dirty flags.
         *
         * Unlike reseedBoundFields (which PRESERVES user edits), this discards
         * the cached value AND the dirty flag, then re-seeds from `selectedRow`
         * (null ? the field's blank type default). Use it to present a screen as
         * a blank/fresh form � e.g. an "Add new record" DAL action � so a value
         * left over from a prior edit (kept dirty in the cache) or from a
         * previously-selected row no longer lingers. Fields are left non-dirty,
         * so a later ancestor-row change still seeds them via reseedBoundFields.
         *
         * `objNames` (component ids) limits the reset to specific fields; omit to
         * reset every binding-driven field on the screen.
         */
        resetBoundFields: (
            state,
            action: PayloadAction<{
                key: string
                selectedRow?: Record<string, unknown> | null
                objNames?: string[]
            }>,
        ) => {
            const { key, selectedRow = null, objNames } = action.payload
            const entry = state.screensByKey[key]
            if (entry) resetEntryBoundFields(entry, selectedRow, objNames)
        },
        /**
         * Enter "new record" mode for a screen (an "Add" DAL action). Flags the
         * key so DynamicScreen seeds it from a null row (blank form, ignoring the
         * grid's auto-selected row), and resets any already-cached value/edit so
         * a stale value can't linger. The flag is stored separately from the
         * screen entry, so it also takes effect on a screen not yet cached.
         */
        enterNewRecordMode: (
            state,
            action: PayloadAction<{
                key: string
                seedRow?: Record<string, unknown> | null
            }>,
        ) => {
            const { key, seedRow = null } = action.payload
            state.newRecordByKey[key] = seedRow
            const entry = state.screensByKey[key]
            if (entry) resetEntryBoundFields(entry, seedRow)
        },
        /** Leave "new record" mode (Save/Cancel) so normal row-seeding resumes. */
        exitNewRecordMode: (state, action: PayloadAction<string>) => {
            delete state.newRecordByKey[action.payload]
        },
        /**
         * Clear ALL "new record" flags � dispatched on an explicit user row-click
         * (see gridActions), which signals the user is now selecting/editing a
         * record rather than adding one, so any pending Add form should re-seed.
         */
        clearAllNewRecordModes: (state) => {
            state.newRecordByKey = {}
        },
        setFieldErrors: (
            state,
            action: PayloadAction<{
                key: string
                errors: Record<string, string[]>
            }>,
        ) => {
            const entry = state.screensByKey[action.payload.key]
            if (entry) entry.fieldErrors = action.payload.errors
        },
        /** Clear dirty flags for a screen (e.g. after a successful save). */
        clearDirtyForScreen: (state, action: PayloadAction<string>) => {
            const entry = state.screensByKey[action.payload]
            if (!entry) return
            for (const field in entry.fields) {
                entry.fields[field].dirty = false
            }
        },
        addLoadingAction: (state, action: PayloadAction<string>) => {
            if (!state.loadingActions.includes(action.payload)) {
                state.loadingActions.push(action.payload)
            }
        },
        removeLoadingAction: (state, action: PayloadAction<string>) => {
            state.loadingActions = state.loadingActions.filter(
                (id) => id !== action.payload,
            )
        },
        /** Drop all cached screens (e.g. BA/language change) to force re-seed. */
        clearScreensForRefetch: (state) => {
            state.screensByKey = {}
            state.activeScreenKey = null
        },
        /** Increment to signal all SDUIGrid instances to re-fetch their data. */
        incrementGridRefreshSeq: (state) => {
            // Coerce non-finite values (e.g. undefined after a persist rehydrate
            // that didn't carry this field) so the counter never becomes NaN.
            state.gridRefreshSeq =
                (Number.isFinite(state.gridRefreshSeq) ? state.gridRefreshSeq : 0) + 1
        },
        /** Increment to signal only the SDUIGrid for a specific DTS to re-fetch. */
        incrementGridRefreshSeqForDts: (state, action: PayloadAction<number>) => {
            const dtsId = action.payload
            const current = state.gridRefreshSeqByDts[dtsId]
            state.gridRefreshSeqByDts[dtsId] =
                (Number.isFinite(current) ? current : 0) + 1
        },
        resetSDUIState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchScreenSchema.pending, (state, action) => {
                const key = `screen:${action.meta.arg}`
                const entry = state.screensByKey[key]
                if (entry) {
                    entry.isLoading = true
                    entry.error = null
                } else {
                    state.screensByKey[key] = {
                        uiSchema: [],
                        metadata: {
                            version: '1.0.0',
                            screenId: action.meta.arg,
                            layoutType: 'grid',
                            cacheKey: key,
                        },
                        fields: {},
                        fieldErrors: {},
                        isLoading: true,
                        error: null,
                    }
                }
            })
            .addCase(fetchScreenSchema.fulfilled, (state, action) => {
                const key = `screen:${action.meta.arg}`
                upsertEntry(state, key, action.payload)
            })
            .addCase(fetchScreenSchema.rejected, (state, action) => {
                const key = `screen:${action.meta.arg}`
                const entry = state.screensByKey[key]
                if (entry) {
                    entry.isLoading = false
                    entry.error = action.payload as string
                }
            })
    },
})

export const {
    upsertScreen,
    setActiveScreenKey,
    updateDataModelField,
    reseedBoundFields,
    resetBoundFields,
    enterNewRecordMode,
    exitNewRecordMode,
    clearAllNewRecordModes,
    setFieldErrors,
    clearDirtyForScreen,
    addLoadingAction,
    removeLoadingAction,
    clearScreensForRefetch,
    incrementGridRefreshSeq,
    incrementGridRefreshSeqForDts,
    resetSDUIState,
} = sduiSlice.actions

// -- Selectors ----------------------------------------------------------------

const EMPTY_MODEL: Record<string, unknown> = {}
const EMPTY_ERRORS: Record<string, string[]> = {}

// Flattened {field: value} cache keyed by the (immutably-replaced) fields object,
// so the same fields reference yields a stable model identity � keeps React.memo
// on LayoutRenderer effective and avoids effect loops.
const flattenCache = new WeakMap<
    Record<string, SDUIFieldState>,
    Record<string, unknown>
>()
const flattenFields = (
    fields: Record<string, SDUIFieldState>,
): Record<string, unknown> => {
    const cached = flattenCache.get(fields)
    if (cached) return cached
    const model: Record<string, unknown> = {}
    for (const field in fields) model[field] = fields[field].value
    flattenCache.set(fields, model)
    return model
}

export const selectScreenEntry =
    (key: string | null) =>
    (state: RootState): SDUIScreenEntry | null =>
        key ? state.sdui?.screensByKey?.[key] ?? null : null

export const selectActiveScreenEntry = (
    state: RootState,
): SDUIScreenEntry | null => {
    const key = state.sdui?.activeScreenKey ?? null
    return key ? state.sdui?.screensByKey?.[key] ?? null : null
}

export const selectScreenDataModel =
    (key: string | null) =>
    (state: RootState): Record<string, unknown> => {
        const entry = key ? state.sdui?.screensByKey?.[key] : null
        return entry ? flattenFields(entry.fields) : EMPTY_MODEL
    }

export const selectScreenFieldErrors =
    (key: string | null) =>
    (state: RootState): Record<string, string[]> => {
        const entry = key ? state.sdui?.screensByKey?.[key] : null
        return entry?.fieldErrors ?? EMPTY_ERRORS
    }

export const selectScreenIsLoading =
    (key: string | null) =>
    (state: RootState): boolean => {
        const entry = key ? state.sdui?.screensByKey?.[key] : null
        return entry?.isLoading ?? false
    }

export const selectScreenError =
    (key: string | null) =>
    (state: RootState): string | null => {
        const entry = key ? state.sdui?.screensByKey?.[key] : null
        return entry?.error ?? null
    }

export const selectLoadingActions = (state: RootState): string[] =>
    state.sdui?.loadingActions ?? []

/** True while a screen is in "new record" mode (Add flow). */
export const selectIsNewRecordMode =
    (key: string | null) =>
    (state: RootState): boolean => {
        if (!key) return false
        const map = state.sdui?.newRecordByKey
        return map != null && key in map
    }

/**
 * The seed row stored when entering new-record mode. `null` means blank form;
 * a plain object means specific fields should be pre-filled (e.g. Licensor).
 */
export const selectNewRecordSeedRow =
    (key: string | null) =>
    (state: RootState): Record<string, unknown> | null =>
        key ? (state.sdui?.newRecordByKey?.[key] ?? null) : null

export default sduiSlice.reducer
